import type { SiteHealthAuditStatusResponse } from '@/lib/siteHealth.types'

export function pageLabel(url: string): string {
  try {
    const parsed = new URL(url)
    const path = parsed.pathname === '/' ? '/' : parsed.pathname

    return `${parsed.host}${path}${parsed.search}`
  } catch {
    return url
  }
}

function pathDepth(url: string): number {
  try {
    return new URL(url).pathname.split('/').filter(Boolean).length
  } catch {
    return Number.MAX_SAFE_INTEGER
  }
}

// Root pages first, then one level deep, and so on; ties fall back to the label spelling.
export function comparePageUrls(left: string, right: string): number {
  const depthDelta = pathDepth(left) - pathDepth(right)

  if (depthDelta !== 0) return depthDelta

  return pageLabel(left).localeCompare(pageLabel(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

export function auditCoverageTarget(
  status: SiteHealthAuditStatusResponse
): number {
  const requestedPages =
    status.requestedPages ?? status.pageLimit ?? status.discoveredPages
  const pageLimit = status.pageLimit ?? 0
  const target = requestedPages > 0 ? requestedPages : pageLimit
  const cappedTarget = pageLimit > 0 ? Math.min(pageLimit, target) : target

  return Math.max(1, cappedTarget, status.receivedPages, status.auditedPages)
}

export function auditCoverageLabel(
  status: SiteHealthAuditStatusResponse
): string {
  return `${status.auditedPages} of ${auditCoverageTarget(status)} pages audited · ${status.receivedPages} received · ${status.failedPages} failed`
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat().format(value)
}

export function pluralize(
  value: number,
  singular: string,
  plural = `${singular}s`
): string {
  return `${formatCount(value)} ${value === 1 ? singular : plural}`
}

export function formatActionType(
  actionType: string | null | undefined
): string {
  if (!actionType) return 'Remediation guidance'

  return actionType
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function explanationParagraphs(
  explanation: string | null | undefined
): string[] {
  return (explanation ?? '')
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0)
}

export function displayDate(value: string | null | undefined): string | null {
  if (!value) return null

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return null

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(date)
}

// The inspector's Details button scrolls to the panel row by this id.
export function findingRowId(findingId: string): string {
  return `inspector-finding-${findingId}`
}
